import { OnInit, AfterViewInit, Component, Input, Injector, Inject, OnDestroy } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Observable, of, Subscription, combineLatest } from 'rxjs';
import { map, filter, tap, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { AddAllRecipientsToListsApiModel } from '../../../../api/distributions/model/add-all-recipients-to-lists.api.model';
import { DistributionApi } from '../../../../api/distributions/distribution.api';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { TargetingListsApiService } from '../../../../api/targeting/targeting-lists.api.service';
import { TargetingMiscApi } from '../../../../api/targeting/targeting-misc.api';

import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { AutocompleteChipsOptions } from '../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';
import {
    BulkActionConfirmationDialogComponent
} from '../../../../Common/Dialogs/components/BulkActionConfirmationDialog/bulk-action-confirmation-dialog.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { DistributionReportStat } from '../../model/statisticsReport/distribution-report-stat-api.model';
import { DistributionStatus } from '../../enum/distribution-status.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SearchModeEnum } from '../../enum/search-mode.enum';
import { TargetingPreferencesService } from '../../services/targeting-preferences.service';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CsvExportDialogComponent } from '../../../../Discover/Targeting/components/TargetingResults/CsvExportDialog/csv-export-dialog.component';
import { DistributionService } from '../../services/distribution.service';
import { DistributionSummaryModel } from '../../model/distribution-summary-model';
import { PortfolioService } from '../../../../Discover/Targeting/components/Portfolios/portfolio-service';
import { RecipientSearchService } from '../../../../Discover/Targeting/components/Distribution/DistributionReport/RecipientSearch/recipient-search.service';
import { SourcePickerService, SourceType } from '../../../../Discover/Targeting/services/source-picker-service';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { TargetingSearchesService } from '../../../../Discover/Targeting/services/targeting-searches.service';
import { TargetingSearchList } from '../../../../Discover/Targeting/models/targeting-search-list.model';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { CommonApiErrorEnum } from '../../../../api/genericApi/enums/common-api-error.enum';
import { TargetingList } from '../../../../Discover/Targeting/models/targeting-list.model';
import {
    CsvInteractionExportDialogComponent
} from '../../../../Discover/Targeting/components/TargetingResults/CsvInteractionExportDialog/csv-interaction-export-dialog.component';
import { TargetingSearch } from  '../../../../Discover/Targeting/models/targeting-search.model';
export enum SourceMode {
    currentlySelectedContacts,
    contactsFrom
}

export enum TargetingBatchActionsEnum {
    export,
    addToList,
    removeFromList,
    distribute,
    exportInteractions
}


@Component({
    selector: 'agpr-targeting-batch-actions-dialog',
    templateUrl: './targeting-batch-actions-dialog.component.html',
    styleUrls: ['./targeting-batch-actions-dialog.component.scss'],
    providers: [
        SourcePickerService,
        {
            provide: FilterService,
            useExisting: SourcePickerService
        },
    ],
    standalone: false
})
export class TargetingBatchActionsDialogComponent extends AbstractDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly defaultBatchActionOptions = [
        {
            value: TargetingBatchActionsEnum.addToList,
            label: 'Discover.batchActions.addToList'
        },
        {
            value: TargetingBatchActionsEnum.removeFromList,
            label: 'Discover.batchActions.removeFromList'
        },
        {
            value: TargetingBatchActionsEnum.export,
            label: 'Discover.batchActions.export'
        },
        {
            value: TargetingBatchActionsEnum.exportInteractions,
            label: 'Discover.batchActions.exportInteractions'
        },
        {
            value: TargetingBatchActionsEnum.distribute,
            label: 'Discover.batchActions.distribute'
        },
    ];

    readonly allInteractionBatchActionOptions = [
        {
            value: TargetingBatchActionsEnum.exportInteractions,
            label: 'Discover.batchActions.exportInteractions'
        }
    ];

    @Input() set selectedEntities(selectedEntites: TargetingResultModel[]) {
        this._selectedEntities = selectedEntites;
        if (this._selectedEntities && this._selectedEntities.length > 0) {
            this.sourceMode = SourceMode.currentlySelectedContacts;
            this.sourceModeChanged();

            this._selectedEntities.forEach((entity) => {
                if (entity.entityType === EntityType.OUTLET) {
                    this.hasOutlets = true;
                } else if (entity.entityType === EntityType.CONTACT) {
                    this.hasContacts = true;
                }
            });
        };
    }

    @Input() set resultsCount(resultsCount: number) {
        this._resultsCount = resultsCount;
        if (this._selectedEntities && this._selectedEntities.length !== this._resultsCount) {
            this.previewCount = this._resultsCount;
        }
    }

    @Input() set initialSourceType(initSource: SourceType) {
        this._initialSourceType = initSource;
        this.selectedSourceType = this.sourceTypes.find(x => x.value === this._initialSourceType) || this.selectedSourceType;
    }

    @Input() set allowDistributionType(value: boolean) {
        if (value && !this.sourceTypes.find(x => x.value === SourceType.distributions)) {
            this.sourceTypes.push(
                {
                    label: 'Discover.batchActions.sourceType.distributions',
                    value: SourceType.distributions
                }
            );
        }
    }
    @Input() entityMode: EntityType = EntityType.CONTACT;
    @Input() allResultsSelected: boolean;
    @Input() initialSourceId: number;
    @Input() listBuilderLists: TargetingListList[];
    @Input() distributionReportStatType: DistributionReportStat;
    @Input() distributionSelectedLink: string;
    @Input() id: string = null;
    @Input() batchActionOptions: DropdownOptionModel[] = this.defaultBatchActionOptions;
    @Input() preSelectedBatchActionValue: TargetingBatchActionsEnum;

    listSelectorHeader: string = 'Discover.batchActions.addToList.label';

    _selectedEntities: TargetingResultModel[];
    _resultsCount: number;
    _initialSourceType: SourceType;

    sourceMode: SourceMode = SourceMode.contactsFrom;
    selectedBatchAction: DropdownOptionModel = this.batchActionOptions[0];
    sources: DropdownOptionModel[];
    selectedSource: DropdownOptionModel;
    sourceTypes: DropdownOptionModel[] = [
        {
            label: 'Discover.recipientEditor.labels.searches',
            value: SourceType.searches
        },
        {
            label: 'Discover.recipientEditor.labels.lists',
            value: SourceType.lists
        },
        {
            label: 'Discover.batchActions.sourceType.allPrivateContacts',
            value: SourceType.allPrivateContacts
        },
        {
            label: 'Discover.batchActions.sourceType.allInteractions',
            value: SourceType.allInteractions
        },
        /*
        {
            label: 'Core.navigation.advDiscover.portfolios',
            value: SourceType.portfolios
        },*/
    ];
    selectedSourceType: DropdownOptionModel = this.sourceTypes[0];

    subscriptions: Subscription[] = [];
    searches: TargetingSearchList[];
    lists: TargetingListList[];
    distributions: DistributionSummaryModel[] = [];
    selectedLists: TargetingListList[] = [];
    previousRequest: Subscription;
    previewCount: number = null;
    previewCountLoading = false;
    contactCount: number = 0;
    outletCount: number = 0;
    hasOutlets: boolean = false;
    hasContacts: boolean = false;
    isDistributionRecipientExport: boolean = false;

    loading: boolean = false;

    csvExportLimit: number;

    distributionDrafts: DropdownOptionModel<DistributionSummaryModel>[];
    selectedDistribution: DropdownOptionModel<DistributionSummaryModel>;
    useExistingDistro = false;
    readonly unSupportedViewSourceTypes = [SourceType.allInteractions];

    readonly listAutocompleteOptions: AutocompleteCustomOption[] = [{
        label: 'Discover.batchActions.listAutocomplete.newListLabel',
        action: (listName: string) => {
            if (listName.length === 0) {

                return;
            }

            this.targetingListService.isNameDuplicated$(listName).pipe(
                switchMap((duplicate) => {
                    if (duplicate) {
                        throw new Error(CommonApiErrorEnum.duplicateName);
                    } else {
                        const list = new TargetingList({name: listName});
                        return this.targetingListService.create(list);
                    }
                })
            ).subscribe({
                next: (id) => {
                    this.selectedLists.push(<TargetingListList>{
                        id: id,
                        name: listName
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.newList.duplicate',
                    });
                }
            });
        },
        appearCondition: AppearCondition.noExactMatchInSuggestions,
        clearOnSelect: true
    }];
    readonly autocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.listFetcher.bind(this),
        dropdown: true,
        customOptions: this.listAutocompleteOptions
    };
    readonly messageSeverities = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly sourceModes = SourceMode;
    readonly sourceTypeEnums = SourceType;
    readonly entityTypeEnum = EntityType;
    readonly batchActionsEnum = TargetingBatchActionsEnum;


    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public coreDialogService: CoreDialogService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public targetingPreferencesService: TargetingPreferencesService,
        public targetingSearchesService: TargetingSearchesService,
        public targetingListService: TargetingListService,
        public sourcePickerService: SourcePickerService,
        public targetingSearchService: TargetingSearchService,
        public targetingListsApiService: TargetingListsApiService,
        public distributionService: DistributionService,
        public distributionApi: DistributionApi,
        public targetingMiscApi: TargetingMiscApi,
        public portfolioService: PortfolioService,
        public recipientSearchService: RecipientSearchService,
        protected injector: Injector
    ) {
        super(coreDialogService, 'TargetingBatchActions');
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.targetingSearchesService.all$,
                this.targetingListService.all$,
                this.distributionService.getDistributionSummaries()
            ]).subscribe(([searches, lists, distributions]) => {
                this.searches = searches.sort((a, b) => a.name.localeCompare(b.name));
                this.distributions = distributions.sort((a, b) => a.name.localeCompare(b.name));
                this.lists = lists;
                this.sourcePickerService.addListsToFilters(lists);

                if (
                    this.preSelectedBatchActionValue === TargetingBatchActionsEnum.removeFromList &&
                    this._initialSourceType === SourceType.lists &&
                    this.initialSourceId &&
                    this.selectedLists.length === 0
                ) {
                    const currentList = lists.find(l => l.id === this.initialSourceId);
                    if (currentList) {
                        this.selectedLists = [currentList];
                    }
                }

                if (!this.sources) {
                    // For initialization, we need to update the source list, populate the filters, and perform any additional setup tasks
                    this.sourceTypeChanged();
                } else {
                    // Only update the source list
                    this.updateSources();
                }
            }),
            this.targetingPreferencesService.targetingPreferences$.pipe(filter(x => !!x)).subscribe(prefs => {
                this.csvExportLimit = prefs.csvExportLimit ? prefs.csvExportLimit : 0;
            }),
            this.sourcePickerService.getItemCount().subscribe(x => {
                if (this.sourceMode === SourceMode.contactsFrom) {
                    this.previewCount = x;
                }
            }),
            this.sourcePickerService.entityCount$.pipe(filter(x => !!x)).subscribe(x => {
                this.contactCount = x.contactCount;
                this.outletCount = x.outletCount;
            }),
            this.sourcePickerService.getSearchLoading().subscribe(x => {
                this.previewCountLoading = x;
            }),
            this.distributionService.getDistributionSummaries().subscribe(distros => {
                this.distributionDrafts = distros
                    .filter(x => x.status === DistributionStatus.draft)
                    .map(x => new DropdownOptionModel(x.name, x));
                this.distributionDrafts.sort((x1, x2) => x2.value.createdDate.getTime() - x1.value.createdDate.getTime());
            })
        );
        this.listSelectorHeader = this.entityMode === EntityType.CONTACT ?
            'Discover.batchActions.addToList.label' : 'Discover.batchActions.addToOutletList.label';
    }

    ngAfterViewInit() {
        if (this.batchActionOptions?.length > 0) {
            if (this.preSelectedBatchActionValue !== undefined) {
                const matchingOption = this.batchActionOptions.find(option =>
                    option.value === this.preSelectedBatchActionValue
                );

                if (matchingOption) {
                    this.selectedBatchAction = matchingOption;
                } else {
                    this.selectedBatchAction = this.batchActionOptions[0];
                }
            } else {
                this.selectedBatchAction = this.batchActionOptions[0];
            }
            this.batchActionChanged();
        }
    }

    /**
     * exports contacts as CSV file
     */
    exportContacts(): void {
        this.coreDialogService.open(CsvExportDialogComponent, {
            inputs: {
                selectedEntities:
                    this.sourceMode === SourceMode.currentlySelectedContacts
                    && !this.allResultsSelected
                        ? this._selectedEntities
                        : null,
                title: this.determineTitle(),
                allSelected: this.allResultsSelected,
                hasContacts: this.hasContacts,
                hasOutlets: this.hasOutlets,
                isDistributionRecipientExport: this.isDistributionRecipientExport,
                distributionId: this.initialSourceId,
                distributionReportStatType: this.distributionReportStatType,
                distributionSelectedLink: this.distributionSelectedLink,
                distributionQuery: this.recipientSearchService.getCurrentSearchContext()
            },
            outputs: {
                dialogClose: (done) => {
                    if (done) {
                        this.closeDialog(true);
                    }
                }
            }
        }, this.injector);
    }

    exportInteractions(): void {
        this.coreDialogService.open(CsvInteractionExportDialogComponent, {
            inputs: {
                selectedEntities:
                    this.sourceMode === SourceMode.currentlySelectedContacts
                    && !this.allResultsSelected
                        ? this._selectedEntities
                        : null,
                allSelected: this.allResultsSelected,
                isAllInteractions: this.selectedSourceType.value === SourceType.allInteractions,
                sourceName: this.selectedSource?.label
            },
            outputs: {
                dialogClose: (done) => {
                    if (done) {
                        this.closeDialog(true);
                    }
                }
            }
        }, this.injector);
    }

    confirmAddToList() {
        let message = '';
        if (this.selectedLists.length > 1) {
            message = this.localizationService.transform('Discover.batchActionsConfirmation.add.multiple', {
                count: this.selectedLists.length,
                entity: this.entityMode === EntityType.CONTACT ? 'contacts' : 'outlets'
            });
        } else {
            message = this.localizationService.transform('Discover.batchActionsConfirmation.add', {
                name: this.selectedLists[0].name,
                entity: this.entityMode === EntityType.CONTACT ? 'contacts' : 'outlets'
            });
        }

        this.showDefaultConfirmationDialog({action: message}, this.addToList);
    }

    addToList(): void {
        const listIds = this.selectedLists.map(list => list.id);

        if (!this.allResultsSelected && this.sourceMode === SourceMode.currentlySelectedContacts){
            // deal with selected contacts/outlets
            this.loading = true;
            const entityIds = this._selectedEntities.map(entity => entity.toEntityIdModel());
            this.targetingListService.addEntitiesToLists(listIds, entityIds).subscribe({
                next: res =>  this.afterListUpdated(),
                error: error => this.afterListUpdatedOnError(error?.error)
            });
        } else {
            let query, addToListRequest;
            if (this.sourceMode === SourceMode.contactsFrom) {
                query = this.sourcePickerService.getCurrentSearchContext();
                addToListRequest = this.targetingListService.addSearchResultsToList(listIds, query);
            } else {
                // this.sourceMode === SourceMode.currentlySelectedContacts
                if (this._initialSourceType === SourceType.distributions) {
                    query = this.recipientSearchService.getCurrentSearchContext();
                    const statType = this.distributionReportStatType;
                    const link = this.distributionSelectedLink;
                    const addRecipientsToListsContext = {
                        query: query,
                        statsType: statType,
                        link: link,
                        listIds: listIds
                    } as AddAllRecipientsToListsApiModel;
                    addToListRequest = this.distributionApi.addAllRecipientsToLists(this.initialSourceId, addRecipientsToListsContext);
                } else {
                    query = this.targetingSearchService.getCurrentSearchContext();
                    addToListRequest = this.targetingListService.addSearchResultsToList(listIds, query);
                }
            }
            if (query) {
                this.loading = true;
                addToListRequest.subscribe({
                    next: done => this.afterListUpdated(),
                    error: error => this.afterListUpdatedOnError(error?.error)
                });
            }
        }
    }

    addToListAndGotoDistribution() {
        let request: Observable<void>;
        if (!this.allResultsSelected && this.sourceMode === SourceMode.currentlySelectedContacts) {
            request = this.targetingListsApiService.createListForDistributionIndividualRecipients().pipe(
                tap(() => this.loading = true),
                switchMap(listId => {
                    const entityIds = this._selectedEntities.map(entity => entity.toEntityIdModel());
                    return this.targetingListService.addEntitiesToLists([listId], entityIds).pipe(
                        tap(() => this.gotoDistribution(listId))
                    );
                }),
            );
        } else {
            request = this.targetingListsApiService.createListForDistributionIndividualRecipients().pipe(
                switchMap(listId => {
                    if (this.allResultsSelected && this.sourceMode === SourceMode.currentlySelectedContacts) {
                        const addRecipientsToListsContext = {
                            query: this.recipientSearchService.getCurrentSearchContext(),
                            statsType: this.distributionReportStatType,
                            link: this.distributionSelectedLink,
                            listIds: [listId]
                        } as AddAllRecipientsToListsApiModel;

                        this.loading = true;
                        if (this._initialSourceType === SourceType.distributions) {
                            return this.distributionApi.addAllRecipientsToLists(this.initialSourceId, addRecipientsToListsContext).pipe(
                                tap(() => this.gotoDistribution(listId))
                            );
                        } else {
                            const searchContext = this.targetingSearchService.getCurrentSearchContext();
                            return this.targetingListService.addSearchResultsToList([listId], searchContext).pipe(
                                tap(() => this.gotoDistribution(listId))
                            );
                        }
                    }

                    if (this.sourceMode === SourceMode.contactsFrom) {
                        const query = this.sourcePickerService.getCurrentSearchContext();

                        this.loading = true;
                        return this.targetingListService.addSearchResultsToList([listId], query).pipe(
                            tap(() => this.gotoDistribution(listId))
                        );
                    }

                    return of(null);
                })
            );
        }

        request.subscribe({
            error: () => {
                this.loading = false;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.batchActions.createDist.error'
                });
            }
        });
    }

    gotoDistribution(listId: number) {
        if (this.selectedDistribution) {
            this.distributionApi.addRecipientList(this.selectedDistribution.value.id, listId).subscribe(done => {
                window.open(this.appBaseHref + `Discover/distributions/distribution/${this.selectedDistribution.value.id}`, '_blank');
                this.closeDialog();
            });
        } else {
            window.open(this.appBaseHref + `Discover/distributions/new?withRecipientList=${listId}`, '_blank');
            this.closeDialog();
        }
    }

    confirmRemoveFromList(){
        let message = '';
        if (this.selectedLists.length > 1) {
            message = this.localizationService.transform('Discover.batchActionsConfirmation.remove.multiple',{
                count: this.selectedLists.length,
                entity: this.entityMode === EntityType.CONTACT ? 'contacts' : 'outlets'
            });
        } else {
            message = this.localizationService.transform('Discover.batchActionsConfirmation.remove', {
                name: this.selectedLists[0].name,
                entity: this.entityMode === EntityType.CONTACT ? 'contacts' : 'outlets'
            });
        }

        this.showDefaultConfirmationDialog({action: message}, this.removeFromList);
    }

    removeFromList(): void {
        const listIds = this.selectedLists.map(list => list.id);
        if (!this.allResultsSelected && this.sourceMode === SourceMode.currentlySelectedContacts){
            this.loading = true;
            // deal with selected contacts/outlets
            const entityIds = this._selectedEntities.map(entity => entity.toEntityIdModel());

            this.targetingListService.removeEntitiesFromLists(listIds, entityIds).subscribe(res => {
                this.afterListUpdated();
            });
        }  else {
            let query, removeFromListRequest;
            if (this.sourceMode === SourceMode.contactsFrom) {
                query = this.sourcePickerService.getCurrentSearchContext();
                removeFromListRequest = this.targetingListService.removeSearchResultFromLists(listIds, query);
            } else {
                // this.sourceMode === SourceMode.currentlySelectedContacts
                if (this._initialSourceType === SourceType.distributions) {
                    query = this.recipientSearchService.getCurrentSearchContext();
                    const statType = this.distributionReportStatType;
                    const link = this.distributionSelectedLink;
                    removeFromListRequest = this.distributionApi.removeAllRecipientsFromLists(this.initialSourceId, query, statType, link, listIds);
                } else {
                    query = this.targetingSearchService.getCurrentSearchContext();
                    removeFromListRequest = this.targetingListService.removeSearchResultFromLists(listIds, query);
                }
            }
            if (query) {
                this.loading = true;
                removeFromListRequest.subscribe(done => this.afterListUpdated());
            }
        }
    }

    private afterListUpdated(): void {
        this.loading = false;

        if (this.selectedBatchAction.value === TargetingBatchActionsEnum.addToList) {
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: this.localizationService.transform('Discover.batchActions.addToList.growlHeading', {
                    entity: this.entityMode === EntityType.CONTACT ? 'Contacts' : 'Outlets'
                }),
                detail: this.localizationService.transform('Discover.batchActions.addToList.growlMessage', {
                    entityNum: this.previewCount,
                    listNum: this.selectedLists.length,
                    entity: this.entityMode === EntityType.CONTACT ? 'contacts' : 'outlets'
                })
            });
        } else {
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: this.localizationService.transform('Discover.batchActions.removeFromList.growlHeading', {
                    entity: this.entityMode === EntityType.CONTACT ? 'Contacts' : 'Outlets'
                }),
                detail: this.localizationService.transform('Discover.batchActions.removeFromList.growlMessage', {
                    entityNum: this.previewCount,
                    listNum: this.selectedLists.length,
                    entity: this.entityMode === EntityType.CONTACT ? 'contacts' : 'outlets'
                })
            });
        }

        this.targetingSearchService.doSearch(true, true);
        this.closeDialog();
    }

    private afterListUpdatedOnError(errorMessage: string): void {
        this.loading = false;

        if (this.selectedBatchAction.value === TargetingBatchActionsEnum.addToList) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Discover.batchActions.addToList.growlHeading', {
                    entity: this.entityMode === EntityType.CONTACT ? 'Contacts' : 'Outlets'
                }),
                detail: errorMessage
            });
        } else {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Discover.batchActions.removeFromList.growlHeading', {
                    entity: this.entityMode === EntityType.CONTACT ? 'Contacts' : 'Outlets'
                }),
                detail: errorMessage
            });
        }

        this.targetingSearchService.doSearch(true, true);
        this.closeDialog();
    }

    sourceModeChanged() {
        if (this.sourceMode === SourceMode.contactsFrom) {
            this.previewCount = null;
            const source = this.sourceTypes.find(x => x.value === this._initialSourceType);

            if (source) {
                this.selectedSourceType = source;
            } else {
                this.selectedSourceType = {
                    label: 'Discover.recipientEditor.labels.searches',
                    value: SourceType.searches
                };
            }
        } else {
            this.previewCount = this.allResultsSelected ? this._resultsCount : this._selectedEntities.length;
        }

        this.sourceTypeChanged();
    }

    private updateSources(): void {
        switch(this.selectedSourceType.value) {
            case SourceType.lists:
                this.sources = this.lists?.map(list => ({
                    label: list.name,
                    value: list
                }));
                break;
            case SourceType.searches:
                this.sources = this.searches?.map(search => ({
                    label: search.name,
                    value: search
                }));
                break;
            case SourceType.distributions:
                this.sources = this.distributions?.map(search => ({
                    label: search.name,
                    value: search
                }));
                break;
            case SourceType.allPrivateContacts:
                this.sourcePickerService.setAllPrivateContacts();
                break;
            case SourceType.portfolios:
                this.sources = this.portfolioService.portfolios?.map(portfolio => {
                    const list  = new TargetingListList();
                    list.id = portfolio.targetingListId;
                    return {
                        label: portfolio.metadata.displayName,
                        value: list
                    };
                });
                break;
            case SourceType.allInteractions:
                this.sourcePickerService.setAllInteractions();
                break;
        }
    }

    sourceTypeChanged() {
        if (!this.selectedSourceType) {
            return;
        }

        this.sourcePickerService.clearFilters();

        this.updateSources();

        this.updateDistributionExportFlag();

        if (this.selectedSourceType.value === SourceType.allPrivateContacts) {
            this.hasContacts = true;
            this.hasOutlets = false;
        }

        if (this.selectedSourceType.value === SourceType.allInteractions) {
            this.batchActionOptions = this.allInteractionBatchActionOptions;
            this.selectedBatchAction =  this.allInteractionBatchActionOptions[0];
            this.previewCount = null;
        } else {
            this.batchActionOptions = this.defaultBatchActionOptions;
        }

        if ((this.selectedSourceType.value === SourceType.allPrivateContacts || this.initialSourceId) && this._initialSourceType === this.selectedSourceType.value) {
            this.sourcePickerService.populateFiltersFromSearch(this.targetingSearchService.generateFiltersSearch());
            this.selectedSource = this.sources.find(x => x.value.id === this.initialSourceId);
            this.sourceChanged();
        } else {
            this.selectedSource = null;
            this.sourcePickerService.clearSearch();
        }

    }

    sourceChanged() {
        if (this.selectedSourceType.value === SourceType.searches) {
            this.sourcePickerService.setActiveSearch(this.selectedSource.value);
            this.hasContacts = this.selectedSource.value.type === SearchModeEnum.contact;
            this.hasOutlets = this.selectedSource.value.type === SearchModeEnum.outlet;
        } else if (this.selectedSourceType.value === SourceType.distributions) {
            this.sourcePickerService.setActiveDistribution(this.selectedSource.value);
            this.hasContacts = true;
            this.hasOutlets = true;
        } else {
            this.sourcePickerService.setActiveList(this.selectedSource.value);
            this.hasContacts = this.selectedSource.value.hasContacts;
            this.hasOutlets = this.selectedSource.value.hasOutlets;
        }
    }

    cancel(): void {
        this.closeDialog(false);
    }

    batchActionChanged(): void {
        switch (this.selectedBatchAction.value) {
            case TargetingBatchActionsEnum.addToList:
                this.listSelectorHeader = this.entityMode === EntityType.CONTACT ?
                    'Discover.batchActions.addToList.label' : 'Discover.batchActions.addToOutletList.label';
                break;
            case TargetingBatchActionsEnum.removeFromList:
                this.listSelectorHeader = this.entityMode === EntityType.CONTACT ?
                    'Discover.batchActions.removeFromList.label' : 'Discover.batchActions.removeOutletFromList.label';
                break;
            case TargetingBatchActionsEnum.export:
                this.updateDistributionExportFlag();
        }
    }

    viewSource($event: Event) {
        const currentSourceType = this.selectedSourceType?.value;
        let url = '';
        switch (currentSourceType) {
            case SourceType.searches:
                url = this.appBaseHref + `Discover/targetingsearches/search/${this.selectedSource.value.id}/results`;
                break;
            case SourceType.lists:
                url = this.appBaseHref + `Discover/targetinglists/list/${this.selectedSource.value.id}/results`;
                break;
            case SourceType.allPrivateContacts:
                url = this.appBaseHref + 'Discover/privatecontacts';
                break;
            case SourceType.distributions:
                url = this.appBaseHref + `Discover/distributions/distribution/${this.selectedSource.value.id}/report`;
                break;
        }

        if (url) {
            const targetingSearch: TargetingSearch = this.sourcePickerService.generateFiltersSearch();
            const fitlerString = TargetingSearch.toFilterQueryString(targetingSearch);
            url += `?filters=${fitlerString}`;
            window.open(url);
        }

        ($event?.currentTarget as HTMLElement)?.blur();
    }

    get disabledViewSourceButton() {
        const currentSourceType = this.selectedSourceType?.value;

        if (currentSourceType == null) {
            return true;
        }

        if (this.unSupportedViewSourceTypes.includes(currentSourceType)) {
            return true;
        }

        if ([SourceType.searches, SourceType.lists, SourceType.distributions].includes(currentSourceType)) {
            const sourceId = !!this.selectedSource?.value?.id;
            return !sourceId;
        }

        return false;
    }

    private updateDistributionExportFlag(): void {
        this.isDistributionRecipientExport =
            (this.sourceMode === SourceMode.currentlySelectedContacts && this._initialSourceType === SourceType.distributions)
            || (this.sourceMode === SourceMode.contactsFrom && this.selectedSourceType.value === SourceType.distributions);
    }

    private listFetcher(query: string): Observable<TargetingListList[]> {
        if (!query) {
            return this.targetingListService.all$;
        } else {
            return this.targetingListService.all$.pipe(
                map(lists => lists.filter(list => list.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()))));
        }
    }

    /**
     * Displays confirmation dialog
     */
    private showDefaultConfirmationDialog(actionLabel, callback: Function): void {
        const dialogIO = {
            inputs: {
                message: 'Discover.batchActionsConfirmation.confirm',
                messageParameters: actionLabel,
                itemCountLabel: this.entityMode === EntityType.CONTACT
                    ? 'Discover.batchActions.contactsSelected' : 'Discover.batchActions.outletsSelected',
                okLabel: this.selectedBatchAction.label,
                itemCount: this.previewCount
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        callback.bind(this)();
                    }
                }
            }
        };
        this.coreDialogService.open(BulkActionConfirmationDialogComponent, dialogIO);
    }

    private determineTitle(): string {
        if (this.selectedSource) {
            return (this.selectedSource.value as { name: string }).name;
        }

        // If no selected source, we are either using an adhoc search, the private contact list, or a subset of a specific input search/list
        if (!this.initialSourceId || this.sourceMode !== SourceMode.currentlySelectedContacts) {
            return '';
        }

        // We are using a subset of a specific input search/list
        const source: { name: string } = this.sources.find(x => x.value.id === this.initialSourceId)?.value;

        return source?.name ?? '';
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
